import React, { useEffect, useState, lazy, Suspense, useRef, useMemo, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import LazyImage from "../components/LazyImage";
import "./SearchResults.css";

// Lazy load components for better initial load time
const AnimeCard = lazy(() => import("../components/AnimeCard"));
const MangaCard = lazy(() => import("../components/MangaCard"));
const CharacterCard = lazy(() => import("../components/CharacterCard"));
const UserCard = lazy(() => import("../components/UserCard"));

// Get API URL from environment variables
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';

// Result card skeleton component
const CardSkeleton = () => (
  <div className="result-card-skeleton">
    <div className="skeleton-image"></div>
    <div className="skeleton-content">
      <div className="skeleton-block" style={{ height: '20px', width: '80%' }}></div>
      <div className="skeleton-block" style={{ height: '16px', width: '60%' }}></div>
    </div>
  </div>
);

// SVG icons for empty states - memoized to prevent re-renders
const EmptyIcon = React.memo(() => (
  <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" fill="currentColor" viewBox="0 0 16 16">
    <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0-14a8 8 0 1 0 0 16A8 8 0 0 0 8 1z"/>
    <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z"/>
  </svg>
));

// Search results page skeleton UI
const SearchResultsSkeleton = () => (
  <div className="search-results-page">
    <div className="search-results-page-title-container">
      <h1 className="search-results-page-title">Search Results</h1>
      <div className="skeleton-block" style={{ height: '20px', width: '40%', marginTop: '10px' }}></div>
    </div>
    
    <div className="results-container">
      {[1, 2, 3, 4].map(sectionIndex => (
        <section key={sectionIndex} className="results-section skeleton">
          <div className="skeleton-block" style={{ height: '28px', width: '40%', marginBottom: '20px' }}></div>
          
          <div className="results-grid">
            {[1, 2, 3, 4].map(cardIndex => (
              <CardSkeleton key={cardIndex} />
            ))}
          </div>
        </section>
      ))}
    </div>
  </div>
);

// Lazy loaded result section component with intersection observer
const LazyResultSection = ({ title, results, cardType, EmptyState }) => {
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '100px' }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.disconnect();
      }
    };
  }, []);

  // Prepare the card component based on type
  const CardComponent = useMemo(() => {
    switch (cardType) {
      case 'anime': return AnimeCard;
      case 'manga': return MangaCard;
      case 'character': return CharacterCard;
      case 'user': return UserCard;
      default: return AnimeCard;
    }
  }, [cardType]);

  return (
    <section className="results-section" ref={sectionRef}>
      <h3 className="results-category">
        {title}
        {results.length > 0 && <span className="section-count">{results.length}</span>}
      </h3>
      
      {results.length > 0 ? (
        visible ? (
          <div className="results-grid">
            <Suspense fallback={
              <>
                {[1, 2, 3, 4].map(i => <CardSkeleton key={i} />)}
              </>
            }>
              {results.map((item) => (
                <CardComponent 
                  key={item.mal_id || item._id} 
                  {...{ [cardType]: item }} 
                />
              ))}
            </Suspense>
          </div>
        ) : (
          <div className="results-grid">
            {[1, 2, 3, 4].map(i => <CardSkeleton key={i} />)}
          </div>
        )
      ) : (
        <div className="no-results-column">
          <EmptyIcon />
          <div className="no-results-text">No {title.toLowerCase()} found</div>
        </div>
      )}
    </section>
  );
};

const SearchResults = () => {
  const [searchParams] = useSearchParams();
  const [searchData, setSearchData] = useState({
    animeResults: [],
    mangaResults: [],
    characterResults: [],
    userResults: []
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const query = searchParams.get("query") || "";
  
  // Extract data for readability
  const { animeResults, mangaResults, characterResults, userResults } = searchData;

// Then update your fetchResults function:
const fetchResults = useCallback(async (searchQuery) => {
  setIsLoading(true);
  setError(null);
  
  try {
    const [animeResponse, mangaResponse, characterResponse, userResponse] = await Promise.all([
      fetch(`${API_BASE_URL}/api/anime/search?query=${encodeURIComponent(searchQuery)}`),
      fetch(`${API_BASE_URL}/api/manga/search?query=${encodeURIComponent(searchQuery)}`),
      fetch(`${API_BASE_URL}/api/characters/search?query=${encodeURIComponent(searchQuery)}`),
      fetch(`${API_BASE_URL}/api/users/search?query=${encodeURIComponent(searchQuery)}`)
    ]);

    // Check if any responses failed
    if (!animeResponse.ok || !mangaResponse.ok || !characterResponse.ok || !userResponse.ok) {
      throw new Error("One or more search API requests failed");
    }

    const animeData = await animeResponse.json();
    const mangaData = await mangaResponse.json();
    const characterData = await characterResponse.json();
    const userData = await userResponse.json();
    
    const newSearchData = {
      // Handle both array and paginated response formats
      animeResults: Array.isArray(animeData) ? animeData : animeData.data || [],
      mangaResults: Array.isArray(mangaData) ? mangaData : mangaData.data || [],
      characterResults: Array.isArray(characterData) ? characterData : characterData.data || [],
      userResults: Array.isArray(userData) ? userData : userData.data || []
    };
    
    setSearchData(newSearchData);
    
  } catch (error) {
    console.error("Search error:", error);
    setError(error.message || "Failed to fetch search results");
  } finally {
    setIsLoading(false);
  }
}, []);
  // Effect to fetch data when query changes
  useEffect(() => {
    if (query) {
      fetchResults(query);
    } else {
      setIsLoading(false);
    }
  }, [query, fetchResults]);

  // Check if there are any results
  const hasResults = useMemo(() => 
    animeResults.length > 0 || 
    mangaResults.length > 0 || 
    characterResults.length > 0 || 
    userResults.length > 0, 
    [animeResults, mangaResults, characterResults, userResults]
  );

  // Loading state with skeleton UI
  if (isLoading) {
    return <SearchResultsSkeleton />;
  }

  // Error state
  if (error) {
    return (
      <div className="search-results-page">
        <div className="search-results-page-title-container">
          <h1 className="search-results-page-title">Search Results</h1>
        </div>
        <div className="error-container">
          <h3>Error searching for "{query}"</h3>
          <p>{error}</p>
          <button onClick={() => fetchResults(query)} className="retry-button">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // No query state
  if (!query) {
    return (
      <div className="search-results-page">
        <div className="search-results-page-title-container">
          <h1 className="search-results-page-title">Search</h1>
        </div>
        <div className="no-results" style={{ marginTop: "2rem" }}>
          <div className="no-results-text">Enter a search term to find anime, manga, characters and users</div>
        </div>
      </div>
    );
  }

  return (
    <div className="search-results-page">
      {/* Page Title */}
      <div className="search-results-page-title-container">
        <h1 className="search-results-page-title">Search Results</h1>
        <p className="search-results-page-subtitle">
          Results for "{query}"
        </p>
      </div>
      
      {hasResults ? (
        <div className="results-container">
          {/* Users Section with lazy loading */}
          <LazyResultSection 
            title="Users" 
            results={userResults} 
            cardType="user" 
          />

          {/* Anime Section with lazy loading */}
          <LazyResultSection 
            title="Anime" 
            results={animeResults} 
            cardType="anime" 
          />

          {/* Character Section with lazy loading */}
          <LazyResultSection 
            title="Characters" 
            results={characterResults} 
            cardType="character" 
          />

          {/* Manga Section with lazy loading */}
          <LazyResultSection 
            title="Manga" 
            results={mangaResults} 
            cardType="manga" 
          />
        </div>
      ) : (
        <div className="no-results">
          <EmptyIcon />
          <div className="no-results-text">No results found for "{query}"</div>
        </div>
      )}
    </div>
  );
};

export default React.memo(SearchResults);